import type { MRT_ColumnDef } from '@talentmesh/table';
import { useMemo } from 'react';
import GuidHelper from '../../../../../../../Utils/GuidHelper';
import { TextCell } from '../../../../../Overview/Contexts/CandidatesTableContext/cells';
import { IdentifiedProfileResponse } from '../../../../api/DTO/IdentifiedProfileResponse';
import { useSearchCriteria } from '../../../../api/useSearchCriteria';
import CompanyCell from '../../../TableCells/CompanyCell';
import CandidateCell from './CandidateCell';
import PersonalInfoCell from './PersonalInfoCell';
import ScoreCell from '@/Components/Table/ScoreCell';
import InfoTooltip from '@/Components/Tooltip/InfoTooltip';
import UIStrings from '@/Utils/UIStrings';

export type IdentifiedCandidatesCol = MRT_ColumnDef<IdentifiedProfileResponse>;

const useColumns = () => {
    const { data } = useSearchCriteria();

    return useMemo(() => {
        const columns: IdentifiedCandidatesCol[] = [
            {
                accessorKey: 'profile.name',
                header: UIStrings.Candidate,
                enableSorting: false,
                Cell: ({ row }) => <CandidateCell {...row.original} />,
                size: 200,
            },
            {
                id: 'personalInfo',
                header: UIStrings.PersonalInfo,
                Cell: ({ row }) => <PersonalInfoCell {...row.original} />,
                size: 130,
            },
            {
                id: 'company',
                header: UIStrings.Company,
                Cell: ({ row }) => <CompanyCell {...row.original} />,
                size: 300,
            },
            {
                accessorKey: 'profile.position',
                header: UIStrings.JobTitle,
                Cell: ({ row }) => (
                    <TextCell value={row.original.profile.experience?.at(0)?.title || row.original.profile.position} />
                ),
                size: 300,
                enableSorting: false,
            },
            {
                id: 'location',
                accessorFn: (row) => row.profile.currentJobLocationGeocode?.title,
                header: UIStrings.Location,
                Cell: ({ row, renderedCellValue }) => (
                    <TextCell value={renderedCellValue || row.original.profile.locationGeocode?.title} />
                ),
                size: 300,
                enableSorting: false,
            },
            {
                accessorKey: 'matchScore',
                header: UIStrings.MatchScore,
                Cell: ({ row }) => (
                    <ScoreCell level={row.original.matchScore?.scoreLevel} score={row.original.matchScore?.score} />
                ),
                size: 120,
            },
        ];

        if (data?.criteria.length) {
            for (let i = 0; i < data.criteria.length; i++) {
                // When recruitment is created without criteria then guid will be empty for all columns.
                // This will cause key duplication issue on the table. To avoid that we will use index based key when guid is empty.
                const criteriaColId =
                    data.criteria[i].id !== GuidHelper.Empty ? data.criteria[i].id : `criteriaScore-${i}`;

                columns.push({
                    id: criteriaColId,
                    accessorFn: (row) => row.searchCriteriaScores?.[i]?.score,
                    header: `${UIStrings.Criteria(i + 1)}`,
                    Header: ({ column }) => (
                        <div className="flex items-center gap-1">
                            <InfoTooltip description={data.criteria[i].criterion} />
                            {column.columnDef.header}
                        </div>
                    ),
                    Cell: ({ row }) => {
                        const criteriaRow = row.original.searchCriteriaScores?.find(
                            ({ searchCriteriaId }) => searchCriteriaId === criteriaColId,
                        );

                        return <ScoreCell level={criteriaRow?.scoreLevel} score={criteriaRow?.score} />;
                    },
                    size: 130,
                });
            }
        }

        return columns;
    }, [data]);
};

export default useColumns;
